// =============================================================================
// svgUtils — czyste operacje na tekscie SVG (logo brandowe).
// Bez zaleznosci od DOM — testowalne w vitest (environment: node).
// =============================================================================

/** Biale wypelnienie w kilku notacjach, ktore potrafi wypluc Recraft. */
const WHITE_FILL = /fill\s*=\s*["']\s*(#fff|#ffffff|white|rgb\(\s*255\s*,\s*255\s*,\s*255\s*\))\s*["']/i;

/** Ile pierwszych ksztaltow bierzemy pod uwage szukajac tla (tlo rysuje sie pierwsze). */
const BACKGROUND_LOOKAHEAD = 2;

/**
 * Usuwa prostokat/sciezke tla, zeby logo mialo przezroczystosc.
 *
 * SVG z natury nie ma tla, ale Recraft potrafi dorzucic bialy <rect> jako PIERWSZY
 * element. Szukamy wiec bialego ksztaltu tylko wsrod dwoch pierwszych elementow —
 * dzieki temu biale detale W SRODKU logo (np. przeswit w symbolu) zostaja nietkniete.
 *
 * Idempotentna: gdy model nie dodal tla, zwraca wejscie bez zmian.
 */
export function stripSvgBackground(svg: string): string {
  const re = /<(rect|path)\b[^>]*?(?:\/>|>\s*<\/\1>)/gi;
  let match: RegExpExecArray | null;
  let seen = 0;

  while ((match = re.exec(svg)) !== null) {
    if (seen >= BACKGROUND_LOOKAHEAD) break;
    seen++;
    if (WHITE_FILL.test(match[0])) {
      return svg.slice(0, match.index) + svg.slice(match.index + match[0].length);
    }
  }
  return svg;
}

/**
 * Przygotowuje SVG do rasteryzacji w danym rozmiarze.
 *
 * Bez jawnych width/height przegladarka renderuje SVG w domyslnym 300x150 —
 * favicon wyszedlby rozciagniety. Dokladamy tez viewBox (gdy go brak) i xmlns
 * (wymagany, gdy SVG ladujemy jako <img src="data:...">).
 */
export function normalizeSvgForRaster(svg: string, size: number): string {
  const found = svg.match(/<svg\b[^>]*>/i);
  if (!found) return svg;

  const original = found[0];
  let tag = original;

  const widthAttr = tag.match(/\bwidth\s*=\s*["']([^"']+)["']/i)?.[1];
  const heightAttr = tag.match(/\bheight\s*=\s*["']([^"']+)["']/i)?.[1];
  const hasViewBox = /\bviewBox\s*=/i.test(tag);

  tag = tag.replace(/\s+width\s*=\s*["'][^"']*["']/gi, "");
  tag = tag.replace(/\s+height\s*=\s*["'][^"']*["']/gi, "");

  if (!hasViewBox) {
    const w = parseFloat(widthAttr || "");
    const h = parseFloat(heightAttr || "");
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      tag = tag.replace(/^<svg/i, `<svg viewBox="0 0 ${w} ${h}"`);
    }
  }

  if (!/\bxmlns\s*=/i.test(tag)) {
    tag = tag.replace(/^<svg/i, `<svg xmlns="http://www.w3.org/2000/svg"`);
  }

  tag = tag.replace(/^<svg/i, `<svg width="${size}" height="${size}"`);
  return svg.replace(original, tag);
}

/**
 * SVG -> data URI (base64, UTF-8 safe).
 *
 * MUSI byc data:, NIE blob: — CSP panelu ma `img-src 'self' data: https:`,
 * wiec blob: URL w <img> zostalby zablokowany (nginx.conf). Dodatkowo goly
 * btoa() wywala sie na polskich znakach, stad przejscie przez TextEncoder.
 */
export function svgToDataUrl(svg: string): string {
  const bytes = new TextEncoder().encode(svg);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return `data:image/svg+xml;base64,${btoa(binary)}`;
}
